import { useEffect } from 'react';

import config from '../../config';
import {
  generateOrganizationSchemaForGraph,
  generateCollectionPageSchemaForGraph,
  generateBreadcrumbListSchemaForGraph,
  generateSuppliersItemListSchemaForGraph,
  generateBlogArticlesItemListSchemaForGraph,
  generateFAQSchemaForGraph,
} from '../utils/schema-org';
import { UseListingSchemasProps } from '../types/schema-org';

export function useListingSchemas({
  breadcrumbSchema,
  suppliers,
  pageTitle,
  pageUrl,
  reviews,
  faqs,
  blogArticles,
}: UseListingSchemasProps) {
  useEffect(() => {
    // Early return if schemas are disabled
    if (!config.enableSchemas) {
      return;
    }

    // Remove any existing schema script
    const existingScript = document.getElementById('schema-graph');
    if (existingScript) existingScript.remove();

    // Create the graph array to hold all schema objects
    const graph = [];

    // Add Organization schema
    graph.push(generateOrganizationSchemaForGraph());

    // Add CollectionPage schema
    if (pageTitle && pageUrl) {
      graph.push(generateCollectionPageSchemaForGraph(pageTitle, pageUrl));
    }

    // Add BreadcrumbList schema if provided
    if (breadcrumbSchema && pageUrl) {
      const breadcrumbData = generateBreadcrumbListSchemaForGraph(
        breadcrumbSchema,
        pageUrl
      );
      if (breadcrumbData) {
        graph.push(breadcrumbData);
      }
    }

    // Add suppliers as ItemList if available
    if (suppliers && suppliers.length > 0 && pageTitle && pageUrl) {
      graph.push(
        generateSuppliersItemListSchemaForGraph(suppliers, pageTitle, pageUrl)
      );
    }

    // Add blog articles as ItemList if available
    if (blogArticles && blogArticles.length > 0 && pageUrl) {
      graph.push(
        generateBlogArticlesItemListSchemaForGraph(blogArticles, pageUrl)
      );
    }

    // Add FAQ schema if available
    if (faqs && faqs.length > 0 && pageUrl) {
      graph.push(generateFAQSchemaForGraph(faqs, pageUrl));
    }

    // Create single script with all schemas in @graph
    const script = document.createElement('script');
    script.id = 'schema-graph';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': graph,
    });

    document.head.appendChild(script);

    return () => {
      // Cleanup schema script
      const element = document.getElementById('schema-graph');
      if (element) element.remove();
    };
  }, [
    breadcrumbSchema,
    suppliers,
    pageTitle,
    pageUrl,
    reviews,
    faqs,
    blogArticles,
  ]);
}
